/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import {
    Position,
    Box,
    Columns,
    Column,
    Strong,
    Text,
    Heading,
    Input
} from '@locus-taxy/neo-ui';
import { useTranslation, type TFunction } from 'react-i18next';
import Checkbox from 'common/components/core/checkbox';
import theme from './TerritoryConfig.scss';
import TerritoryNavigationBar from 'common/entity/territoryMaster/TerritoryNavigationBar';
import { type PlanConfigConstantsModelType } from 'common/models/flow/CreateTerritoryConfig';

const CONFIG_TYPE = Object.freeze({
    outletNumberFairness: 'outletNumberFairness',
    minTerritory: 'minTerritory',
    maxTerritory: 'maxTerritory',
    revenueFairness: 'revenueFairness',
    tatFairness: 'tatFairness',
    name: 'name'
});

const useTerritoryConfig = (): ({
    config: PlanConfigConstantsModelType,
    name: string,
    onChangeConfig: (
        type: $Keys<PlanConfigConstantsModelType> | 'name',
        value: boolean | string
    ) => void
}) => {
    const [outletNumberFairness, setOutletNumberFairness] = React.useState(
        true
    );
    const [revenueFairness, setRevenueFairness] = React.useState(false);
    const [tatFairness, setTatFairness] = React.useState(true);
    const [name, setName] = React.useState('');
    const [minTerritory, setMinTerritory] = React.useState(2);
    const [maxTerritory, setMaxTerritory] = React.useState(5);

    const onChangeConfig = React.useCallback((type, value) => {
        switch (type) {
            case CONFIG_TYPE.outletNumberFairness:
                setOutletNumberFairness(Boolean(value));
                break;
            case CONFIG_TYPE.minTerritory:
                setMinTerritory(value);
                break;
            case CONFIG_TYPE.maxTerritory:
                setMaxTerritory(value);
                break;
            case CONFIG_TYPE.revenueFairness:
                setRevenueFairness(Boolean(value));
                break;
            case CONFIG_TYPE.tatFairness:
                setTatFairness(Boolean(value));
                break;
            case CONFIG_TYPE.name:
                setName(String(value));
                break;

            default:
                break;
        }
    }, []);

    return {
        config: {
            outletNumberFairness,
            revenueFairness,
            tatFairness,
            maxTerritory,
            minTerritory
        },
        name,
        onChangeConfig
    };
};

type Props = {
    onNext: ({ config: PlanConfigConstantsModelType, name: string }) => void,
    onPrev: () => void,
    onDismiss: () => void
};

const MinMaxInput = (props: {
    t: TFunction,
    onChange: (key: 'minTerritory' | 'maxTerritory', value: number) => void,
    minValue: number,
    maxValue: number
}) => {
    const { onChange } = props;
    const _onChangeMinTerritory = React.useCallback(
        e => {
            if (!isNaN(+e.target.value)) {
                onChange(CONFIG_TYPE.minTerritory, +e.target.value);
            }
        },
        [onChange]
    );
    const _onChangeMaxTerritory = React.useCallback(
        e => {
            if (!isNaN(+e.target.value)) {
                onChange(CONFIG_TYPE.maxTerritory, +e.target.value);
            }
        },
        [onChange]
    );
    return (
        <Columns space="small">
            <Input
                placeholder={props.t(
                    'createTerritory.territoryLimits.minPlaceholder'
                )}
                label={props.t('createTerritory.territoryLimits.min')}
                value={props.minValue}
                onChange={_onChangeMinTerritory}
            />
            <Input
                placeholder={props.t(
                    'createTerritory.territoryLimits.maxPlaceholder'
                )}
                label={props.t('createTerritory.territoryLimits.max')}
                value={props.maxValue}
                onChange={_onChangeMaxTerritory}
            />
        </Columns>
    );
};

const TerritoryConfig = (props: Props): React.Node => {
    const { t } = useTranslation('entityManagement');
    const territoryConfigState = useTerritoryConfig();

    const TERRITORY_CONFIG_STRUCTURE: Array<{
        label: string,
        note: string,
        valueKey: $Keys<PlanConfigConstantsModelType>,
        disabled?: boolean
    }> = React.useMemo(() => {
        return Object.freeze([
            {
                label: t('createTerritory.numberOfOutlets'),
                note: t('createTerritory.numberOfOutletsNote'),
                valueKey: CONFIG_TYPE.outletNumberFairness
            },
            {
                label: t('createTerritory.revenue'),
                note: t('createTerritory.revenueNote'),
                valueKey: CONFIG_TYPE.revenueFairness
            }
        ]);
    }, [t]);

    const { onNext } = props;

    const onNextConfig = React.useCallback(() => {
        onNext({
            config: territoryConfigState.config,
            name: territoryConfigState.name
        });
    }, [onNext, territoryConfigState.config, territoryConfigState.name]);

    const onTerritoryLimitsChange = React.useCallback(
        (key, value) => {
            territoryConfigState.onChangeConfig(key, value);
        },
        [territoryConfigState]
    );

    return (
        <Position
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            flexDirection="column"
            bg="white"
            display="flex"
            zIndex={15}
        >
            <TerritoryNavigationBar
                onNextClick={onNextConfig}
                nextDisabled={
                    !territoryConfigState.name ||
                    !territoryConfigState.config.minTerritory ||
                    !territoryConfigState.config.maxTerritory
                }
                onPrevClick={props.onPrev}
                onCancelClick={props.onDismiss}
                titleText={t('createTerritory.selectParameters')}
            />

            <Box p="3rem 1.2rem">
                <Columns>
                    <Column width="1/3">
                        <Heading level="4">
                            <Strong>
                                {t('createTerritory.selectConstraints')}
                            </Strong>
                        </Heading>
                        <Text fontSize={2}>
                            {t('createTerritory.canEditMultipleParams')}
                        </Text>
                    </Column>
                    <Column width="1/3">
                        {TERRITORY_CONFIG_STRUCTURE.map(config => {
                            return (
                                <Box key={config.valueKey}>
                                    <Checkbox
                                        {...config}
                                        checked={
                                            territoryConfigState.config[
                                                config.valueKey
                                            ]
                                        }
                                        theme={theme}
                                        className={theme.checkBox}
                                        onChange={value =>
                                            territoryConfigState.onChangeConfig(
                                                config.valueKey,
                                                value
                                            )
                                        }
                                    />
                                    <Text
                                        fontSize={1}
                                        ml="2.8rem"
                                        maxWidth="60%"
                                    >
                                        {config.note}
                                    </Text>
                                </Box>
                            );
                        })}
                    </Column>
                </Columns>
                <Box mt="2.8rem">
                    <Columns>
                        <Column width="1/3">
                            <Heading level="4">
                                <Strong>
                                    {t('createTerritory.identifier')}
                                </Strong>
                            </Heading>
                        </Column>
                        <Column width="1/3">
                            <Input
                                value={territoryConfigState.name}
                                placeholder={t(
                                    'createTerritory.identifierPlaceholder'
                                )}
                                onChange={e =>
                                    territoryConfigState.onChangeConfig(
                                        CONFIG_TYPE.name,
                                        e.target.value || ''
                                    )
                                }
                            />
                        </Column>
                    </Columns>
                </Box>
                <Box mt="2.8rem">
                    <Columns>
                        <Column width="1/3">
                            <Heading level="4">
                                <Strong>
                                    {t('createTerritory.territoryLimitLabel')}
                                </Strong>
                            </Heading>
                        </Column>
                        <Column width="1/3">
                            <MinMaxInput
                                t={t}
                                minValue={
                                    territoryConfigState.config.minTerritory
                                }
                                maxValue={
                                    territoryConfigState.config.maxTerritory
                                }
                                onChange={onTerritoryLimitsChange}
                            />
                        </Column>
                    </Columns>
                </Box>
            </Box>
        </Position>
    );
};

export default TerritoryConfig;
